import { useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { AppShell } from 'components/dashboard/app-shell'
import { MailHeader } from 'components/mail/mail-header'
import { Button } from 'components/ui/button'
import { Label } from 'components/ui/label'
import { useUser } from 'contexts/UserContext'
import { listMailDrafts, listMailboxes, MailDraft, Mailbox } from 'services/mail'
export default function MailDraftsPage() {
  const { user, loading } = useUser()
  const [items, setItems] = useState<MailDraft[]>([]), [boxes, setBoxes] = useState<Mailbox[]>([])
  const [filter, setFilter] = useState(''), [page, setPage] = useState(1), [more, setMore] = useState(false)
  const [busy, setBusy] = useState(true), [error, setError] = useState('')
  const lock = useRef<number | null>(null), generation = useRef(0)
  const load = async (requested: number, context = generation.current) => {
    if (lock.current === context) return
    lock.current = context; setBusy(true); setError('')
    try {
      const result = await listMailDrafts(requested, 25)
      if (context !== generation.current) return
      setItems(previous => [...previous, ...result.results.filter(item => !previous.some(existing => existing.id === item.id))])
      setMore(!!result.next); setPage(requested + 1)
    } catch (e) { if (context === generation.current) setError((e as Error).message || 'Impossibile caricare le bozze.') }
    finally { if (lock.current === context) lock.current = null; if (context === generation.current) setBusy(false) }
  }
  useEffect(() => {
    const context = ++generation.current
    setItems([]); setBoxes([]); setPage(1); setMore(false); setFilter('')
    if (!loading && user?.enabled_modules?.emails) {
      void load(1, context)
      listMailboxes().then(result => { if (context === generation.current) setBoxes(result.results) }).catch(e => { if (context === generation.current) setError((e as Error).message) })
    }
    return () => { generation.current++ }
  }, [loading, user?.id, user?.enabled_modules?.emails])
  if (loading) return <p role="status">Caricamento accesso…</p>
  if (!user) return <Navigate to="/login" replace />
  if (!user.enabled_modules?.emails) return <Navigate to="/dashboard-new" replace />
  const visible = items.filter(item => !filter || item.mailbox === filter)
  return <AppShell currentUser={user}><div className="mail-page">
    <MailHeader title="Bozze" description="Riprendi una composizione o consulta un invio." />
    <Label htmlFor="draft-filter">Filtra tra gli elementi caricati</Label><select id="draft-filter" className="rounded border p-2" value={filter} onChange={event => setFilter(event.target.value)}>
      <option value="">Tutte le caselle</option>{boxes.map(box => <option value={box.id} key={box.id}>{box.name || box.address}</option>)}
    </select>
    {error && <div role="alert"><p>{error}</p><Button onClick={() => load(page)} disabled={busy}>Riprova</Button></div>}
    {busy && <p role="status">Caricamento bozze…</p>}
    {!busy && !error && !visible.length && <p>{more ? 'Nessuna corrispondenza tra gli elementi caricati' : filter ? 'Nessuna corrispondenza tra gli elementi caricati' : 'Nessuna bozza o invio salvato.'}</p>}
    {(['draft', 'confirmed'] as const).map(status => <section key={status} aria-label={status === 'draft' ? 'Bozze modificabili' : 'Invii e verifiche'} className="my-5 space-y-3">
      <h2 className="text-lg font-semibold">{status === 'draft' ? 'Bozze' : 'Invii e verifiche'}</h2>
      <ul className="space-y-3">{visible.filter(item => item.status === status).map(item => <li className="rounded border p-3 min-w-0 break-words" key={item.id}>
        <Link className="font-semibold underline" to={`/mail/compose?draft=${encodeURIComponent(item.id)}`}>{item.subject || 'Senza oggetto'}</Link>
        <p>{[...item.to, ...item.cc, ...item.bcc].join(', ')}</p><p>{boxes.find(box => box.id === item.mailbox)?.address || 'Casella non disponibile'}</p>
        <p className="whitespace-pre-wrap">{Array.from(item.body_text).slice(0, 120).join('')}</p>
        {status === 'confirmed' && <p>Consulta esito e verifiche. Composizione in sola lettura.</p>}
      </li>)}</ul>
    </section>)}
    {more && <Button disabled={busy} onClick={() => load(page)}>Carica altre bozze</Button>}
  </div></AppShell>
}
